import {
  selectCurrentPlayer,
  selectElementalsConstructionTokens,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectRound
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {selectSelectedToken} from "../../../redux/reducers/privateStateReducer/selectors";
import {calculateConstructionCells} from "./calculateConstructionCells";
import {checkIfConstructionAllowedInRound} from "./checkIfConstructionAllowedInRound";
import {checkIfHasRemainingConstructionActions} from "./checkIfHasRemainingConstructionActions";
import {getBuildingPrice} from "../../economy";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {FACTIONS} from "../../../constants/factions";
import {getElementForTokenType} from "../../factions/elementals/getElementForTokenType";
import {getUnusedElementIndex} from "../../factions/elementals/getUnusedElementIndex";
import {MINE_EXPLORATION} from "../../../configs/factions/dwarves";
import {TEXT_NO_ACTIONS_REMAINING, TEXT_NOT_ALLOWED_FIRST_ROUND_BUILDINGS} from "../../../constants/text/construction";

export const CONSTRUCTION_PLACEMENT_KINDS = {
  // Строение не выбрано или ячейка вне подсвеченных для строительства
  OUTSIDE: 'outside',
  // Строить здесь нельзя: errorMessage
  ERROR: 'error',
  // Обычное строение (setHexToken)
  COMMON: 'common',
  // Специальное строение (setSpecialBuilding)
  SPECIAL: 'special',
  // Разведка рудника гномов (бросок d6 и setHexToken с фиксацией хода)
  MINE_EXPLORATION: 'mine_exploration',
};

export const TEXT_CELL_NOT_AVAILABLE_FOR_CONSTRUCTION =
  'Клетка недоступна для строительства!\n\nМожно строить только на выделенных белым контуром клетках.';
export const TEXT_NO_FREE_ELEMENTS = 'Нет свободных элементов для этого строения!';
export const TEXT_NOT_ENOUGH_COINS_FOR_CONSTRUCTION = 'Недостаточно монет для строительства!';

/**
 * Решение по размещению строения на ячейке — без побочных эффектов. Те же
 * проверки и в том же порядке, что делает клик по полю в фазе строительства
 * (GameScene.handleConstructionPhaseClick): строит текущий игрок состояния
 * выбранным строением (selectSelectedToken). Планирование в чужой ход и
 * применение плана в начале своего хода подставляют соответствующее состояние
 * — правила от этого у всех трех одни и те же.
 *
 * Возвращает {kind, token, errorMessage, priceCoins, isFreeAction, elementIndex}
 * (elementIndex — индекс элемента Элементалей для траты или null).
 */
export const resolveConstructionPlacement = (state, hexKey) => {
  const selectedToken = selectSelectedToken(state);
  if (!selectedToken) {
    return {kind: CONSTRUCTION_PLACEMENT_KINDS.OUTSIDE, token: null};
  }

  // Нужна одна ячейка — и считается только она (ответ тот же, что у полного
  // расчета, см. calculateConstructionCells)
  const {
    availableCells,
    isSpecialBuildingSelected,
    blockedCells,
    blockedCellsErrorMessages,
  } = calculateConstructionCells(state, {targetHexKey: hexKey});

  if (!availableCells.includes(hexKey) && !blockedCells.has(hexKey)) {
    return {kind: CONSTRUCTION_PLACEMENT_KINDS.OUTSIDE, token: selectedToken};
  }

  const round = selectRound(state);
  const currentPlayer = selectCurrentPlayer(state);
  const pendingBuildings = selectPendingBuildings(state);
  const nextInitiativePlayerId = selectNextInitiativePlayerId(state);
  const isInitiativeInterceptedWithIsolationism = selectIsInitiativeInterceptedWithIsolationism(state);

  const {priceCoins, isFreeAction} = getBuildingPrice(selectedToken, currentPlayer, pendingBuildings);
  const hasEnoughCoins = currentPlayer.coins >= priceCoins;
  const hasActionsRemaining = checkIfHasRemainingConstructionActions(
    currentPlayer, pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism
  );

  let elementIndex = null;
  if (isPlayerFaction(currentPlayer, FACTIONS.ELEMENTALS)) {
    const elementToUse = getElementForTokenType(selectedToken);
    if (elementToUse) {
      elementIndex = getUnusedElementIndex(elementToUse, selectElementalsConstructionTokens(state));
    }
  }

  // Порядок как в клике: запрет первого раунда показывается, только если нет
  // ни одной из проблем ниже — у них приоритет
  let errorMessage;
  if (!checkIfConstructionAllowedInRound(selectedToken, round)) {
    errorMessage = TEXT_NOT_ALLOWED_FIRST_ROUND_BUILDINGS;
  }
  if (elementIndex === -1) {
    errorMessage = TEXT_NO_FREE_ELEMENTS;
  } else if (!availableCells.includes(hexKey)) {
    errorMessage = TEXT_CELL_NOT_AVAILABLE_FOR_CONSTRUCTION;
  } else if (blockedCells.has(hexKey)) {
    errorMessage = blockedCellsErrorMessages.get(hexKey);
  } else if (!isFreeAction && !hasActionsRemaining) {
    errorMessage = TEXT_NO_ACTIONS_REMAINING;
  } else if (!hasEnoughCoins) {
    errorMessage = TEXT_NOT_ENOUGH_COINS_FOR_CONSTRUCTION;
  }

  if (errorMessage) {
    return {kind: CONSTRUCTION_PLACEMENT_KINDS.ERROR, token: selectedToken, errorMessage};
  }

  let kind = CONSTRUCTION_PLACEMENT_KINDS.COMMON;
  if (isSpecialBuildingSelected) {
    kind = CONSTRUCTION_PLACEMENT_KINDS.SPECIAL;
  } else if (isPlayerFaction(currentPlayer, FACTIONS.DWARVES) && selectedToken === MINE_EXPLORATION) {
    kind = CONSTRUCTION_PLACEMENT_KINDS.MINE_EXPLORATION;
  }

  return {kind, token: selectedToken, priceCoins, isFreeAction, elementIndex};
};
